import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import drEasyApi from "../Api/Customapi";

// Query keys
export const SHIFTS_QUERY_KEY = ["shifts"];
export const CLINIC_SHIFTS_QUERY_KEY = ["clinic-shifts"];
export const SPECIALISTS_QUERY_KEY = ["specialists"];

// Get shifts by specialist
export const useGetShifts = (specialistId) => {
  return useQuery({
    queryKey: [...SHIFTS_QUERY_KEY, specialistId],
    queryFn: async () => {
      const res = await drEasyApi.get("/api/Specialists/GetShifts", {
        params: { specialistId },
      });
      // Handle the nested response structure: response.data.data
      return res.data?.data || [];
    },
    enabled: !!specialistId,
    onError: (error) => {
      message.error(error.response.data[0] || "Faild");
    },
  });
};

// Get clinic shifts
export const useGetClinicsShifts = (params) => {
  return useQuery({
    queryKey: [...CLINIC_SHIFTS_QUERY_KEY, params],
    queryFn: async () => {
      const res = await drEasyApi.get("/api/Specialists/GetClinicsShifts", {
        params: {
          clinicId: params.clinicId, // API handles null/undefined for "all"
          specialistId: params.specialistId,
          pageNumber: params.pageNumber || 1,
          pageSize: params.pageSize || 20,
        },
      });
      return res.data.clinics || { clinics: [], totalCount: 0 }; // Ensure structure matches
    },
    // Change this to allow the query to run when you want "all" data
    enabled: true,
  });
};

// Get specialists
export const useGetSpecialists = (params = {}) => {
  return useQuery({
    queryKey: [...SPECIALISTS_QUERY_KEY, params],
    queryFn: async () => {
      const res = await drEasyApi.get("/api/Centers/getMySpecialists", {
        params: {
          PageNumber: params.pageNumber || 1,
          PageSize: params.pageSize || 100,
          NameSearch: params.nameSearch || "",
          WorkingDay: params.workingDay,
        },
      });
      return res.data || { specialists: [], totalCount: 0 };
    },
    onError: (error) => {
      message.error(error.response.data[0] || "Failed to fetch specialists");
    },
  });
};

// Create shift mutation
export const useCreateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shiftData) => {
      return await drEasyApi.post("/api/Specialists/CreateShift", {
        shift: shiftData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SHIFTS_QUERY_KEY);
      queryClient.invalidateQueries(CLINIC_SHIFTS_QUERY_KEY);
      message.success("Shift created successfully");
    },
    onError: (err) => {
      const errorMessage = err?.response?.data[0] || "Failed to create shift";
      message.error(errorMessage);
    },
  });
};

// Add shift slot mutation
export const useAddShiftSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slotData) => {
      return await drEasyApi.post("/api/Specialists/AddShiftSlot", slotData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SHIFTS_QUERY_KEY);
      queryClient.invalidateQueries(CLINIC_SHIFTS_QUERY_KEY);
      message.success("Time slot added successfully");
    },
    onError: (err) => {
      const errorMessage = err?.response?.data[0] || "Failed to add time slot";
    },
  });
};

// Update shift slot mutation
export const useUpdateShiftSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slotData) => {
      return await drEasyApi.put("/api/Specialists/UpdateShiftSlot", slotData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SHIFTS_QUERY_KEY);
      queryClient.invalidateQueries(CLINIC_SHIFTS_QUERY_KEY);
      message.success("Time slot updated successfully");
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data[0] || "Failed to update time slot";
      // message.error(errorMessage);
    },
  });
};

// Delete shift slot mutation
export const useDeleteShiftSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slotId) => {
      return await drEasyApi.delete(
        `/api/Specialists/DeleteShiftSlot/${slotId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SHIFTS_QUERY_KEY);
      queryClient.invalidateQueries(CLINIC_SHIFTS_QUERY_KEY);
      message.success("Time slot deleted successfully");
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data[0] || "Failed to delete time slot";
      message.error(errorMessage);
    },
  });
};


// Update shift mutation
export const useUpdateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shiftId, days }) => {
      return await drEasyApi.put("/api/Specialists/UpdateShift", {
        shiftId,
        shift: { shiftId, days },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SHIFTS_QUERY_KEY);
      queryClient.invalidateQueries(CLINIC_SHIFTS_QUERY_KEY);
      message.success("Shift updated successfully");
    },
    onError: (err) => {
      message.error(err?.response?.data?.[0] || "Failed to update shift");
    },
  });
};

// Delete shift mutation
export const useDeleteShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shiftId) => {
      return await drEasyApi.delete(`/api/Specialists/DeleteShift/${shiftId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(SHIFTS_QUERY_KEY);
      queryClient.invalidateQueries(CLINIC_SHIFTS_QUERY_KEY);
      message.success("Shift deleted successfully");
    },
    onError: (err) => {
      message.error(err?.response?.data?.[0] || "Failed to delete shift");
    },
  });
};